import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { ScenarioPill } from '../../../../../common/ui/scenario-pill/index.tsx';
import type { ScenarioNode } from '../../../../../services/scenario-list/index.ts';

import type { scenariosSection_AtlassianConversationAssistantAgent$key } from './__generated__/scenariosSection_AtlassianConversationAssistantAgent.graphql.ts';
import messages from './messages.ts';
import { ScenarioModal } from './scenario-modal/index.tsx';

type ScenariosSectionProps = {
	scenarios: ScenarioNode[];
	cloudUrl?: string;
	scenarioListRef: scenariosSection_AtlassianConversationAssistantAgent$key | null;
	agentSchemaNumber?: number;
} & AssistanceServiceInputParam;

export const ScenariosSection = ({
	scenarios,
	assistanceServiceParams,
	cloudUrl,
	scenarioListRef,
	agentSchemaNumber,
}: ScenariosSectionProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [openedScenario, setOpenedScenario] = useState<ScenarioNode | null>(null);

	const scenarioListFragment =
		useFragment<scenariosSection_AtlassianConversationAssistantAgent$key>(
			graphql`
				fragment scenariosSection_AtlassianConversationAssistantAgent on AgentStudioScenariosResult {
					edges {
						node {
							id
							...scenarioModal_AtlassianConversationAssistantAgent
						}
					}
				}
			`,
			scenarioListRef,
		);

	const isAgentV2 = agentSchemaNumber === 2;
	const activeScenarios = scenarios.filter(
		(scenario) =>
			scenario.isActive && scenario.isValid?.value && !(isAgentV2 && scenario.isDefault),
	);

	if (!activeScenarios?.length) {
		return <Text>{formatMessage(isAgentV2 ? messages.noSubagents : messages.noScenarios)}</Text>;
	}

	return (
		<Stack space="space.050" alignInline="start">
			{activeScenarios.map((scenario) => (
				<ScenarioPill
					name={scenario.name}
					isActive={true}
					isDefault={scenario.isDefault}
					key={scenario.id}
					onClick={() => setOpenedScenario(scenario)}
				/>
			))}

			<ModalTransition>
				{!!openedScenario && (
					<ScenarioModal
						scenario={openedScenario}
						onClose={() => setOpenedScenario(null)}
						assistanceServiceParams={assistanceServiceParams}
						cloudUrl={cloudUrl}
						scenarioRef={
							scenarioListFragment?.edges?.find((edge) => edge?.node?.id === openedScenario.id)
								?.node ?? null
						}
					/>
				)}
			</ModalTransition>
		</Stack>
	);
};
